import React, { useState } from "react";
import Modal from "react-modal";
import { FiX } from "react-icons/fi";
import { useDispatch } from "react-redux";
import { cloneProject } from "../../../Redux/actions/projectActions";
import "../../Dashboard/CreateCustomProjectModal.css";

Modal.setAppElement("#root");

export default function CloneProjectModal({
  projectCloneModalOpen,
  setProjectCloneModalOpen,
  projectId,
  setCloneMsg,
  setSuccessModal,
  setLoading,
  loading = false,
  projectType,
  onProjectLimitError,
}) {
  const [title, setTitle] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [isCloning, setIsCloning] = useState(false);
  const dispatch = useDispatch();
  const busy = loading || isCloning;

  const setBusy = (value) => {
    setIsCloning(value);
    setLoading?.(value);
  };

  const handleCloneProject = async () => {
    if (title.trim() === "") {
      setErrorMsg("Please add a title");
      return;
    }
    if (!projectId) {
      setErrorMsg("Project ID does not exist");
      return;
    }
    setBusy(true);
    const isCard = projectType !== "scroll";
    const res = await dispatch(cloneProject(projectId, title.trim(), isCard));
    if (res?.status === 200 && res?.data?.success) {
      setProjectCloneModalOpen(false);
      setTitle("");
      setErrorMsg("");
      // Prefer server message; avoid leave-behind delete text via parent successMsg
      setCloneMsg?.(
        res?.data?.message || "Project has been successfully created.",
      );
      setSuccessModal?.(true);
      setBusy(false);
    } else {
      const errorMessage =
        res?.response?.data?.message ||
        res?.response?.data?.error ||
        res?.message ||
        "Unable to duplicate project. Please try again.";
      const isProjectLimitError =
        res?.response?.status === 403 ||
        errorMessage.toLowerCase().includes("plan allows only");

      if (isProjectLimitError && onProjectLimitError) {
        setProjectCloneModalOpen(false);
        setTitle("");
        setErrorMsg("");
        onProjectLimitError();
      } else {
        setErrorMsg(errorMessage);
      }
      setBusy(false);
    }
  };

  const closeModalHandle = () => {
    if (busy) return;
    setProjectCloneModalOpen(false);
    setTitle("");
    setErrorMsg("");
    setCloneMsg?.("");
  };

  const onChangeHandler = (e) => {
    setTitle(e.target.value);
    setErrorMsg("");
  };

  const handleKeypress = (e) => {
    if (
      e.key === "Enter" &&
      (e.code === "Enter" || e.code === "NumpadEnter") &&
      title.trim() &&
      !busy
    ) {
      handleCloneProject();
    }
  };

  return (
    <Modal
      isOpen={projectCloneModalOpen}
      onRequestClose={closeModalHandle}
      style={{
        overlay: {
          backgroundColor: "rgba(6, 5, 12, 0.72)",
          backdropFilter: "blur(8px)",
          zIndex: 100,
        },
        content: {
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "min(480px, calc(100vw - 32px))",
          maxHeight: "calc(100vh - 32px)",
          padding: 0,
          border: "1px solid rgba(124, 92, 255, 0.25)",
          borderRadius: "20px",
          background: "#12101c",
          overflow: "visible",
          outline: "none",
        },
      }}
      className="create-custom-project-modal"
    >
      <div className="relative p-6 md:p-7">
        <button
          type="button"
          onClick={closeModalHandle}
          disabled={busy}
          className="absolute top-5 right-5 p-2 rounded-full text-[#625e83] hover:text-white hover:bg-[rgba(255,255,255,0.06)] transition-colors disabled:opacity-40"
          aria-label="Close"
        >
          <FiX className="w-4 h-4" />
        </button>

        <div className="pr-8 mb-6">
          <h2 className="font-body text-[22px] font-semibold text-white tracking-tight">
            Duplicate project
          </h2>
          <p className="font-body text-[14px] text-[#a8a4c8] mt-1.5 leading-relaxed">
            Create a copy of this project with a new name.
          </p>
        </div>

        <div>
          <label
            htmlFor="clone-project-name"
            className="block font-body text-[13px] text-white mb-2"
          >
            Project Name
          </label>
          <input
            id="clone-project-name"
            name="clone-project-name"
            type="text"
            autoFocus
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck={false}
            data-lpignore="true"
            data-form-type="other"
            value={title}
            placeholder="e.g. Indie Short Film Copy"
            onChange={onChangeHandler}
            onKeyDown={handleKeypress}
            disabled={busy}
            className="ccp-input font-body"
          />
          {errorMsg ? (
            <p className="font-body text-[13px] text-[#ff7b8a] mt-2.5 mb-0">
              {errorMsg}
            </p>
          ) : null}
        </div>

        <div className="mt-7 flex items-center gap-3">
          <button
            type="button"
            onClick={closeModalHandle}
            disabled={busy}
            className="flex-1 py-3 rounded-xl font-body text-[15px] font-semibold text-[#a8a4c8] border border-[rgba(124,92,255,0.6)] bg-[rgba(255,255,255,0.02)] hover:text-white hover:border-[#7c5cff] hover:bg-[rgba(124,92,255,0.06)] transition-all disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleCloneProject}
            disabled={!title.trim() || busy}
            style={busy ? { background: "var(--sx-dim, #a8a4c8)" } : undefined}
            className={`flex-1 py-3 rounded-xl font-body text-[15px] font-semibold transition-all disabled:cursor-not-allowed ${
              busy
                ? "text-[#12101c]"
                : "bg-[#7c5cff] text-white hover:bg-[#9d7dff] disabled:opacity-40 hover:enabled:-translate-y-px hover:enabled:shadow-[0_8px_24px_-8px_rgba(124,92,255,0.6)]"
            }`}
          >
            {busy ? "Duplicating..." : "Confirm"}
          </button>
        </div>
      </div>
    </Modal>
  );
}
